import React, { useState, useEffect } from "react";
import { TextField } from "../../../../ui/components/TextField";
import { FeatherSearch } from "@subframe/core";
import {
  ApprovalEntity,
  PaginationInfoEntity,
} from "../../domain/entities/ApprovalEntity";
import { ApprovalCard } from "./ApprovalCard";
import { Loader } from "../../../../ui/components/Loader";

interface ApprovalListProps {
  approvals: ApprovalEntity[];
  pagination: PaginationInfoEntity | null;
  loading: boolean;
  selectedEntityType: string | null;
  /** Current search string from parent (URL / shared state); keeps input in sync with deep links */
  searchQuery: string;
  status: "pending" | "approved" | "rejected" | null;
  onStatusChange: (status: "pending" | "approved" | "rejected" | null) => void;
  onSearch: (search: string) => void;
  onReview: (approval: ApprovalEntity) => void;
  onLoadMore?: () => void;
  isMyRequestsView?: boolean;
  renderCustomCard?: (approval: ApprovalEntity) => React.ReactNode;
}

export const ApprovalList: React.FC<ApprovalListProps> = ({
  approvals,
  pagination,
  loading,
  selectedEntityType,
  searchQuery,
  status,
  onStatusChange,
  onSearch,
  onReview,
  onLoadMore,
  isMyRequestsView = false,
  renderCustomCard,
}) => {
  const [searchValue, setSearchValue] = useState(searchQuery);

  useEffect(() => {
    setSearchValue(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearch(searchValue);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchValue, onSearch]);

  const getEntityTypeDisplay = (): string => {
    const displayMap: Record<string, string> = {
      invoice: "Invoices",
      bill: "Bills",
      customer: "Customers",
      vendor: "Vendors",
      credit_note: "Credit Note",
      debit_note: "Debit Note",
      bank_transaction: "Cash and Bank",
      bank_account: "Bank Accounts",
      journal: "Journal",
      ap_ar_journal: "AP/AR Journal",
      gl: "General Ledger",
      month_end_checklist_item: "Month End Checklist",
      tenant_compliance_calendar: "Compliance",
    };
    return displayMap[selectedEntityType || ""] || "All";
  };

  const getCount = (): number => {
    return pagination?.total || approvals.length;
  };

  const getSearchPlaceholder = (): string => {
    switch (selectedEntityType) {
      case "invoice":
        return "Search by invoice #, customer, amount…";
      case "bill":
        return "Search by bill #, vendor, amount…";
      case "credit_note":
        return "Search by credit note #, customer, amount…";
      case "debit_note":
        return "Search by debit note #, vendor, amount…";
      case "customer":
        return "Search by name, reference, or amount…";
      case "vendor":
        return "Search by name, reference, or amount…";
      default:
        return "Search by reference, party name, or amount…";
    }
  };

  return (
    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6 self-stretch bg-neutral-50 px-6 py-6 overflow-auto">
      <div className="flex w-full items-center justify-between">
        <span className="text-heading-3 font-heading-3 text-default-font">
          {getEntityTypeDisplay()} ({getCount()})
        </span>
        <TextField
          className="h-auto w-80 flex-none"
          label=""
          helpText=""
          icon={<FeatherSearch />}
        >
          <TextField.Input
            placeholder={getSearchPlaceholder()}
            value={searchValue}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSearchValue(event.target.value);
            }}
          />
        </TextField>
      </div>
      <div className="flex w-full flex-col items-start gap-4">
        {loading && approvals.length === 0 ? (
          <div className="flex w-full items-center justify-center py-12">
            <Loader />
          </div>
        ) : approvals.length === 0 ? (
          <div className="flex w-full items-center justify-center py-12">
            <span className="text-body font-body text-subtext-color">
              {isMyRequestsView
                ? "No review requests found"
                : "No approvals found"}
            </span>
          </div>
        ) : (
          approvals.map((approval) =>
            renderCustomCard ? (
              renderCustomCard(approval)
            ) : (
              <ApprovalCard
                key={approval.id}
                id={`approval-${approval.id}`}
                approval={approval}
                onReview={() => onReview(approval)}
              />
            ),
          )
        )}
        {pagination?.hasMore && !loading && (
          <div className="flex w-full items-center justify-center py-4">
            <button
              onClick={onLoadMore}
              className="text-body font-body text-brand-600 hover:text-brand-700"
            >
              Load More
            </button>
          </div>
        )}
        {loading && approvals.length > 0 && (
          <div className="flex w-full items-center justify-center py-4">
            <Loader />
          </div>
        )}
      </div>
    </div>
  );
};
